// alternative-dashboard/alternative-components/AlternativeBalanceOverview.tsx
import { useEffect, useRef } from "react";
import { SvgIcon } from "@progress/kendo-react-common";
import { arrowRotateCwIcon } from "@progress/kendo-svg-icons";
import { useCountry } from "../../../../services/context/CountryContext";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { BalancecontentSkeleton } from "@/app/components/webloading/shimmer/MyprofileLoading";
import { useT } from "@/app/hooks/useT";
import CountrySelector from "../../Dashboard/BalanceOverview/CountrySelector";
import TotalAssetsWidget from "./TotalAssetsWidget";
import AlternativeLiabilities from "./AlternativeLiabilities";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { gxAccountBalanceTotalApi } from "@/app/apiConfig/services/accountsAbstractApiLayer";
import { useQuery } from "@tanstack/react-query";
import { gxDashboardBalanceAccountsApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import "./styles/AlternativStyle.scss";

const isZeroBalance = (value: number): boolean => value === 0;

const AlternativeBalanceOverview = () => {
  const t = useT();
  const { selectedCountry } = useCountry();
  const currencyCode = selectedCountry?.currency?.code ?? "";
  const pollAttemptRef = useRef<number>(0);

  useEffect(() => {
    pollAttemptRef.current = 0;
  }, [currencyCode]);

  const {
    data: balanceData,
    isLoading: loadingBalance,
    error: balanceError,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["balance", currencyCode],
    queryFn: async () => {
      const response = await gxAccountBalanceTotalApi({
        currencyCode,
      });

      if (response?.status === 1) {
        return response.data;
      }

      throw new Error(response?.errorMessage);
    },
    enabled: !!currencyCode,
  });

  const {
    data: dashboardBalanceData,
    isLoading: loadingCashFlow,
    error: cashFlowError,
  } = useQuery({
    queryKey: ["dashboardBalanceAccounts", currencyCode],
    queryFn: async () => {
      const response = await gxDashboardBalanceAccountsApi({
        currencyCode,
      });

      if (response?.status === 1) {
        return response.data;
      }

      throw new Error(response?.errorMessage);
    },
    enabled: !!currencyCode,
  });

  const cashFlowErrorMessage = cashFlowError
    ? t("cash_flow_fetch_error")
    : null;

  const balanceErrorMessage = (() => {
    if (!balanceError) return null;
    if (balanceError instanceof Error) return balanceError.message;
    return "Error fetching balance";
  })();

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;
  const isRefreshing = isFetching;
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const showTotalBalanceDash = isZeroBalance(
    balanceData?.totalbalance?.totalBalance ?? 0,
  );
  const showAssetsBalanceDash = isZeroBalance(
    balanceData?.totalassetsbalance?.totalBalance ?? 0,
  );
  const showLiabilitiesBalanceDash = isZeroBalance(
    balanceData?.totalliablitiesbalance?.totalBalance ?? 0,
  );

  const formatLastUpdated = (date: Date): string => {
    const dateStr = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${dateStr} ${timeStr}`;
  };

  const renderFormattedAmount = (
    amount: number,
    currency: string,
    decimalColor: string = "#FFFFFFB2",
    showSymbolOnly: boolean = false,
  ) => {
    const formatResult = gxCurrencyFormat(amount, currency);

    if (showSymbolOnly) {
      return (
        <>
          <span className="gx-span-currencycode">{formatResult.symbol}</span>{" "}
          {formatResult.integer}
          {formatResult.decimal && (
            <span className="gx-span-currencysymbol">
              {formatResult.decimal}
            </span>
          )}
        </>
      );
    }

    return (
      <>
        {formatResult.integer}
        {formatResult.decimal && (
          <span style={{ color: decimalColor }}>{formatResult.decimal}</span>
        )}
      </>
    );
  };

  const generateAssetsDetails = (casa: number, deposit: number): string =>
    [`${t("accounts")} ${casa}`, `${t("deposits")} ${deposit}`].join(" & ");

  const generateLiabilitiesDetails = (loan: number, card: number): string =>
    [`${t("loans")} ${loan}`, `${t("cards")} ${card}`].join(" & ");

  const handleRefresh = async () => {
    if (isRefreshing) return;
    pollAttemptRef.current = 0;
    await refetch();
  };

  const currencyLabel =
    balanceData?.totalbalance?.currency ??
    selectedCountry?.currency?.code ??
    "AED";
  const totalBalanceAmount = balanceData?.totalbalance?.totalBalance ?? 0;
  const assetsAmount = balanceData?.totalassetsbalance?.totalBalance ?? 0;
  const liabilitiesAmount =
    balanceData?.totalliablitiesbalance?.totalBalance ?? 0;

  const assetsDetails = balanceData?.totalassetsbalance
    ? generateAssetsDetails(
        balanceData.totalassetsbalance.casa,
        balanceData.totalassetsbalance.deposit,
      )
    : "No assets";

  const liabilitiesDetails = balanceData?.totalliablitiesbalance
    ? generateLiabilitiesDetails(
        balanceData.totalliablitiesbalance.loan,
        balanceData.totalliablitiesbalance.card,
      )
    : "No liabilities";

  // Inflows / outflows: always shown as a magnitude with an explicit sign,
  // regardless of how the api signs the raw number
  const inflowAmount = Math.abs(dashboardBalanceData?.inflows ?? 0);
  const outflowAmount = Math.abs(dashboardBalanceData?.outflows ?? 0);
  const netPositionAmount = dashboardBalanceData?.netPosition ?? 0;
  const netPositionIsNegative = netPositionAmount < 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        // empty
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const consentStatus = sessionStorage.getItem("consent_status");
    if (!consentStatus) return;

    try {
      const parsedStatus = JSON.parse(consentStatus);

      if (parsedStatus?.status === "success") {
        pollAttemptRef.current = 0;
        void refetch();
        sessionStorage.removeItem("consent_status");
      }
    } catch (error) {
      console.error("Invalid consent_status in sessionStorage", error);
    }
  }, [refetch]);

  const getRefreshIconClass = (): string =>
    isRefreshing
      ? "cursor-not-allowed opacity-60 animate-spin"
      : "cursor-pointer";

  return (
    <div className="gx-abo-container">
      <div className="gx-abo-wrapper">
        <div className="gx-bal-top-section">
          <div className="gx-abo-main-content">
            <div className="gx-abo-balance-section">
              <div className="gx-abo-balance-wrapper">
                {loadingBalance ? (
                  <BalancecontentSkeleton />
                ) : (
                  <div className="gx-abo-balance-content">
                    <div className="gx-abo-balance-header">
                      <p className="gx-p16-sb gx-total-cash-flow">
                        {t("total_cash_balance")}
                      </p>
                      <div className="gx-abo-country-selector">
                        <CountrySelector />
                      </div>
                    </div>

                    <div className="gx-abo-balance-amount">
                      <h1 className="gx-32-b">
                        {balanceErrorMessage || showTotalBalanceDash
                          ? "-"
                          : renderFormattedAmount(
                              totalBalanceAmount,
                              currencyLabel,
                              "#848484",
                              true,
                            )}
                      </h1>
                    </div>

                    <div className="gx-abo-updated">
                      {lastUpdated && (
                        <p className="gx-p10-r whitespace-nowrap">
                          <span className="mr-0.5 gx-p10-r">
                            {t("balance_as_on")}
                          </span>
                          {formatLastUpdated(lastUpdated)}
                        </p>
                      )}
                      <SvgIcon
                        className={`gx-abo-refresh-icon ${getRefreshIconClass()}`}
                        icon={arrowRotateCwIcon}
                        onClick={handleRefresh}
                      />
                    </div>

                    <div className="gx-abo-divider" />

                    {loadingCashFlow ? (
                      <div className="gx-bene-form-container-loading-overlay">
                        <SubmitLoading />
                      </div>
                    ) : cashFlowErrorMessage ? (
                      <p className="gx-p14-r gx-cashflow-error">
                        {cashFlowErrorMessage}
                      </p>
                    ) : (
                      <div className="gx-abo-cashflow-row">
                        <div className="gx-abo-cashflow-item">
                          <p className="gx-p16-m gx-abo-cashflow-label">
                            {t("inflows")}
                          </p>
                          <p className="gx-p16-b gx-abo-cashflow-value gx-positive">
                            +{" "}
                            {
                              gxCurrencyFormat(inflowAmount, currencyLabel)
                                .integer
                            }
                          </p>
                        </div>
                        <div className="gx-abo-cashflow-item">
                          <p className="gx-p16-m gx-abo-cashflow-label">
                            {t("outflows")}
                          </p>
                          <p className="gx-p16-b gx-abo-cashflow-value gx-negative">
                            -{" "}
                            {
                              gxCurrencyFormat(outflowAmount, currencyLabel)
                                .integer
                            }
                          </p>
                        </div>
                        <div className="gx-abo-cashflow-item">
                          <p className="gx-p16-m gx-abo-cashflow-label">
                            {t("net_position")}
                          </p>
                          <p
                            className={`gx-p16-b gx-abo-cashflow-value ${
                              netPositionIsNegative
                                ? "gx-negative"
                                : "gx-positive"
                            }`}
                          >
                            {netPositionIsNegative ? "-" : "+"}{" "}
                            {
                              gxCurrencyFormat(
                                Math.abs(netPositionAmount),
                                currencyLabel,
                              ).integer
                            }
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="gx-abo-metrics-section">
              <div className="gx-abo-metrics-wrapper">
                <div className="gx-abo-metrics-container gx-abo-metrics-stacked">
                  <TotalAssetsWidget
                    amount={assetsAmount}
                    currencyLabel={currencyLabel}
                    details={assetsDetails}
                    isLoading={loadingBalance}
                    showDash={showAssetsBalanceDash}
                  />
                  <AlternativeLiabilities
                    amount={liabilitiesAmount}
                    currencyLabel={currencyLabel}
                    details={liabilitiesDetails}
                    isLoading={loadingBalance}
                    showDash={showLiabilitiesBalanceDash}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlternativeBalanceOverview;
